/** Cosmetic character choices. These never change physics or recorded inputs. */
export interface Appearance {
  hair: "none" | "crop" | "shag" | "ponytail" | "long" | "mullet" | "mohawk" | "buns";
  hairColor: string;
  headband: "none" | "classic" | "sport" | "long";
  headbandColor: string;
  hat: "none" | "beanie" | "cap";
}

export const HAIRSTYLES = [
  { value: "none", label: "None" },
  { value: "crop", label: "Crop" },
  { value: "shag", label: "Shag" },
  { value: "ponytail", label: "Ponytail" },
  { value: "long", label: "Long" },
  { value: "mullet", label: "Mullet" },
  { value: "mohawk", label: "Mohawk" },
  { value: "buns", label: "Space buns" },
] as const;

export const HAIR_COLORS = [
  { value: "#302c40", label: "Ink" },
  { value: "#744638", label: "Chestnut" },
  { value: "#cb603e", label: "Copper" },
  { value: "#ebc477", label: "Blond" },
  { value: "#c5c7d7", label: "Silver" },
  { value: "#ed70b9", label: "Rose" },
] as const;

export const HEADBAND_STYLES = [
  { value: "none", label: "None" },
  { value: "classic", label: "Classic" },
  { value: "sport", label: "Sport" },
  { value: "long", label: "Long tails" },
] as const;

export const HEADBAND_COLORS = [
  { value: "#ff8c1a", label: "Orange" },
  { value: "#d0342c", label: "Red" },
  { value: "#3d7bff", label: "Blue" },
  { value: "#2fc25f", label: "Green" },
  { value: "#ffc233", label: "Gold" },
  { value: "#9d4edd", label: "Purple" },
  { value: "#2de2ff", label: "Cyan" },
  { value: "#ff2d95", label: "Pink" },
] as const;

export const HATS = [
  { value: "none", label: "None" },
  { value: "beanie", label: "Beanie" },
  { value: "cap", label: "Cap" },
] as const;

export const DEFAULT_APPEARANCE: Readonly<Appearance> = {
  hair: "ponytail",
  hairColor: "#cb603e",
  headband: "classic",
  headbandColor: "#ff8c1a",
  hat: "none",
};

// Retain the original key so existing same-origin builder choices carry into the game.
export const APPEARANCE_STORAGE_KEY = "lache.customization-prototype.v1";

/** Only catalog values survive a reload, including after a prototype catalog changes. */
export function readSavedAppearance(): Appearance | null {
  const result = { ...DEFAULT_APPEARANCE };
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(APPEARANCE_STORAGE_KEY) ?? "null");
    if (!stored || typeof stored !== "object" || Array.isArray(stored)) return null;
    const values = stored as Record<string, unknown>;
    let accepted = 0;
    for (const [key, choices] of Object.entries({
      hair: HAIRSTYLES,
      hairColor: HAIR_COLORS,
      headband: HEADBAND_STYLES,
      headbandColor: HEADBAND_COLORS,
      hat: HATS,
    })) {
      const value = values[key];
      if (typeof value === "string" && choices.some((choice) => choice.value === value)) {
        Object.assign(result, { [key]: value });
        accepted++;
      }
    }
    return accepted > 0 ? result : null;
  } catch {
    // Existing players keep their original look when storage is blocked or invalid.
    return null;
  }
}

export function readAppearance(): Appearance {
  return readSavedAppearance() ?? { ...DEFAULT_APPEARANCE };
}

export function saveAppearance(appearance: Appearance): boolean {
  try {
    localStorage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(appearance));
    return true;
  } catch {
    return false;
  }
}
